import React, { useRef } from 'react'
import { Know as KnowInterface, KnowTranscript } from 'interfaces/Know'
import { useRouter } from 'next/router'
import {
    AnalyticsEventKnowConversionContentBannerButtonClick,
    AnalyticsEventKnowConversionMobileFixedBannerButtonClick,
    AnalyticsEventKnowSubjectsSectionImpression,
} from 'lib/constants/AnalyticsEventType'
import { getReferrerScreen } from 'lib/formatting/url'
import { trackEvent } from 'lib/tracking/analytics-service'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'
import { appDeepLinkKnow } from 'lib/features/app-deep-links'
import Stack from 'components/elements/Stack'
import { RestructuredMobile } from 'components/layouts/Layouts.css'
import theme from 'stories/utils/theme'
import _useTranslation from 'lib/hooks/_useTranslation'
import { parseLocale } from 'lib/constants/localization'
import { getFirstLocalSubjectFromKnowSubjects } from 'lib/formatting/subjects'
import KnowBreadcrumbs from '../SEO/KnowSEOBreadcrumbs'
import KnowSEOTranscript from '../SEO/Transcript/KnowSEOTranscriptEnding'
import KnowSEOTranscriptBeginning from '../SEO/Transcript/KnowSEOTranscriptBeginning'
import WebappKnowMobileFixedConversionBanner from '../SEO/ConversionBottomBanner'
import { Flashcard } from 'interfaces/Flashcard'
import { getMobileOS } from 'lib/tracking/attribution/device'
import SidebarKnows from 'components/modules/Knows/SidebarKnows'
import { SearchOptions } from 'interfaces/TrackingSnippets'
import { getCountryLanguageConfig } from 'lib/constants/countryLanguageConfig'
import useImpressionTracking from 'lib/hooks/useImpressionTracking'
import dynamic from 'next/dynamic'
import MobileKnowContent from './MobileKnowContent'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'

const SEOKnowunityAdvantages = dynamic(() => import('../SEO/SEOKnowunityAdvantages'))
const SEOKnowunitySubjects = dynamic(() => import('../SEO/SEOKnowunitySubjects'))

interface Props {
    knowSeoInfo: KnowSeoInfo | null
    transcript: KnowTranscript | null
    similarKnows: KnowInterface[] | null
    flashcards?: Flashcard[] | null
    isOwner?: boolean
    disableConversion?: boolean
    search?: SearchOptions
}

const MobileKnowSection = ({
    knowSeoInfo,
    transcript,
    similarKnows,
    flashcards,
    isOwner,
    disableConversion,
    search,
}: Props) => {
    const router = useRouter()
    const { locale } = router
    const parsedLocale = parseLocale(locale)
    const { t } = _useTranslation('knows')
    const referrerScreen = getReferrerScreen(router)
    const mobileOS = getMobileOS() ?? 'ios'
    const countryLanguageConfig = getCountryLanguageConfig(locale)
    const isLaunched = !!countryLanguageConfig?.isLaunched

    const { know } = knowSeoInfo || {}

    const localizedSubject = know ? getFirstLocalSubjectFromKnowSubjects(know?.subjects, parsedLocale.countryCode) : null

    const knowSubjectsRef = useRef<HTMLDivElement>(null)
    useImpressionTracking(knowSubjectsRef, AnalyticsEventKnowSubjectsSectionImpression, { referrerScreen, know }, !isOwner)

    if (!know) return null

    const handleContentConversionBannerButtonClick = (platform: 'android' | 'ios') => {
        trackEvent(AnalyticsEventKnowConversionContentBannerButtonClick, {
            know: know ?? undefined,
            useBeacon: true,
            referrerScreen,
        })
        redirectToAppDownloadUrlAsync(platform, 'knowSeoPage', appDeepLinkKnow(know.uuid))
    }

    const handleMobileFixedConversionBannerButtonClick = (platform: 'android' | 'ios') => {
        trackEvent(AnalyticsEventKnowConversionMobileFixedBannerButtonClick, {
            know: know ?? undefined,
            useBeacon: true,
            referrerScreen,
        })
        redirectToAppDownloadUrlAsync(platform, 'knowSeoPage', appDeepLinkKnow(know.uuid))
    }

    return (
        <RestructuredMobile backgroundColor={theme.colors.white}>
            <Stack gutter={0}>
                <KnowBreadcrumbs knowSeoInfo={knowSeoInfo} referrerScreen={referrerScreen} />
            </Stack>
            <Stack>
                {knowSeoInfo ? (
                    <MobileKnowContent
                        knowSeoInfo={knowSeoInfo}
                        isOwner={isOwner}
                        onContentConversionBannerButtonClick={handleContentConversionBannerButtonClick}
                        isMobile
                        downloadDeepLinkPath={appDeepLinkKnow(know.uuid)}
                        transcript={transcript}
                        flashcards={flashcards}
                        showPreview={disableConversion}
                        search={search}
                    />
                ) : null}
                <SidebarKnows
                    parentKnow={know}
                    heading={t('knows/similarContentHeading')}
                    knows={similarKnows ?? []}
                    subject={localizedSubject}
                />
                <KnowSEOTranscriptBeginning transcript={transcript} know={know} />
            </Stack>

            {!isOwner ? (
                <>
                    <SEOKnowunitySubjects objectRef={knowSubjectsRef} parsedLocale={parsedLocale} />
                    <SEOKnowunityAdvantages isLaunched={isLaunched} know={know} />
                </>
            ) : null}
            {!disableConversion && isLaunched && mobileOS ? (
                <WebappKnowMobileFixedConversionBanner
                    onDownloadButtonClick={handleMobileFixedConversionBannerButtonClick}
                    mobileOS={mobileOS}
                    downloadDeepLinkPath={appDeepLinkKnow(know.uuid)}
                    know={know}
                />
            ) : null}
            <KnowSEOTranscript transcript={transcript} know={know} />
        </RestructuredMobile>
    )
}

export default MobileKnowSection
